import { Button, useModal, useToast } from '@serodigital/ui-components';
import { useMutation, useQuery } from '@apollo/client';
import * as React from 'react';
import {
  FirmwareVersionsQueryVariables,
  FirmwareVersions,
  FirmwareVersionsQuery,
  UpgradeFirmware,
} from '../../../../../shared/api-clients/gql';
import { FirmwareUpgradeModal } from '../../../components/modal/firmwareUpgradeModal';
import { useListBees } from '../../../contexts/listBees';
import { UIConfig } from '../../../helpers/UIConfig';

interface FirmwareUpgradeProps {
  selectedBees: string[];
}
export const FirmwareUpgrade = ({ selectedBees }: FirmwareUpgradeProps) => {
  const { selectedHardwareVersion, clearSelectedBees } = useListBees();
  const { isOpen, toggle } = useModal();
  const [firmwareVersions, setFirmwareVersions] = React.useState<string[]>([]);
  const { setToastMessage } = useToast();
  const { data, refetch, loading } = useQuery<FirmwareVersionsQuery, FirmwareVersionsQueryVariables>(FirmwareVersions, {
    variables: { hardwareVersion: selectedHardwareVersion },
  });

  React.useEffect(() => {
    // Refetch firmware version if hardware version has changed
    refetch({
      hardwareVersion: selectedHardwareVersion,
    });
  }, [selectedHardwareVersion]);

  React.useEffect(() => {
    if (!data) return;
    setFirmwareVersions(data?.firmwareVersions?.firmwareVersions);
  }, [data]);

  const [upgradeFirmware] = useMutation(UpgradeFirmware, {
    onCompleted: () => {
      setToastMessage({
        label: 'Firmware Update Request Successfully Sent',
        type: 'success',
        position: 'topCenter',
      });
      clearSelectedBees();
      toggle();
    },
    onError: e => {
      setToastMessage({
        label: e.message,
        type: 'error',
        position: 'topCenter',
      });
    },
  });

  const upgradeFirmwareForMultipleDevices = async selectedFirmwareVersion => {
    await upgradeFirmware({
      variables: {
        upgradeFirmware: { deviceIds: selectedBees, file: selectedFirmwareVersion },
      },
    });
  };

  return (
    <div className={'mb-32'}>
      <div className={'flex'}>
        {selectedBees.length === UIConfig.selectedBEEsLimit ? (
          <span
            className={'flex justify-center items-center mr-24 italic'}
            data-qa="firmware-upgrade-selected-row-count"
          >
            A maximum of 20 BEEs can be selected at a time
          </span>
        ) : (
          <span className={'flex justify-center items-center mr-24'} data-qa="firmware-upgrade-selected-row-count">
            {selectedBees.length} Properties Selected
          </span>
        )}
        <Button disabled={loading} size={'small'} onClick={toggle} data-qa="firmware-upgrade-button">
          Upgrade Firmware
        </Button>
      </div>
      <FirmwareUpgradeModal
        toggle={toggle}
        isOpen={isOpen}
        firmwareVersions={firmwareVersions}
        upgradeFirmware={upgradeFirmwareForMultipleDevices}
      />
    </div>
  );
};
